import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import RemoveIcon from '@mui/icons-material/Remove'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import React, { useState } from 'react'

export interface MenuItemType {
  text: string
  icon?: JSX.Element
  selected?: boolean
  hidden?: boolean
  onClick?: () => void
  items?: MenuItemType[]
}

interface ItemProps {
  menuItem: MenuItemType
  level: number
}

function MenuItem(props: ItemProps): JSX.Element {
  const { menuItem, level } = props
  const isParent = menuItem.items && menuItem.items.length > 0
  const isTopLevel = level === 0

  const [open, setOpen] = useState(() => {
    return isParent && menuItem.selected
  })

  const handleClick = (): void => {
    setOpen(!open)
  }

  if (menuItem.hidden) {
    return <></>
  }

  return (
    <>
      <ListItem>
        <ListItemButton
          selected={!isParent && menuItem.selected}
          onClick={isParent ? handleClick : menuItem.onClick}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              backgroundColor: 'primary.50',
              boxShadow: (theme) => `inset 3px 0 0 0 ${theme.palette.primary.main}`,
              '&:hover': { backgroundColor: 'primary.100' },
              '& .MuiListItemIcon-root': { color: 'primary.700' },
              '& .MuiListItemText-root': { color: 'primary.700' },
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: isTopLevel ? 'grey.800' : 'text.secondary',
              ...(isTopLevel ? {} : { '& .MuiSvgIcon-root': { fontSize: '1.25rem' } }),
            }}
          >
            {menuItem.icon ?? <RemoveIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />}
          </ListItemIcon>
          <ListItemText
            primary={menuItem.text}
            sx={{
              fontSize: '0.875rem',
              fontWeight: isTopLevel ? 500 : 400,
              color: isTopLevel ? 'grey.900' : 'text.secondary',
            }}
            disableTypography
          />
          {isParent && (open ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </ListItem>

      {isParent && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <MenuItemDrawer menuItems={menuItem.items || []} level={level + 1} />
        </Collapse>
      )}
    </>
  )
}

interface ListProps {
  menuItems: MenuItemType[]
  level?: number
}

export default function MenuItemDrawer(props: ListProps): JSX.Element {
  const level = props.level ?? 0
  return (
    <List
      sx={level > 0 ? { ml: 2, pl: 0, borderLeft: '2px solid', borderColor: 'grey.400' } : { pl: 0 }}
    >
      {props.menuItems.map((item) => (
        <MenuItem key={item.text} menuItem={item} level={level} />
      ))}
    </List>
  )
}
